import { cast, flow, getParent, getRoot, types } from 'mobx-state-tree';
import { NewsModel } from '../news-model';
import { MapleMCommuCategory, MapleMCommuId } from 'types/enum';
import { IRootStore } from 'store/root';
import { CmsApi, CommunityApi } from 'service/axios-api';
import { IToyCommuStore } from './index';
import { INewsModel } from '../news';

let mapleMList: any[] = [];

const MapleMStore = types
    .model('MapleMStore', {
        umbracoList: types.array(NewsModel),
        listModel: types.array(NewsModel),
    })
    .views((self) => ({
        get parent(): IToyCommuStore {
            return getParent<IToyCommuStore>(self);
        },
        get root(): IRootStore {
            return getRoot<IRootStore>(self);
        },
        get productId(): string {
            return this.parent.productId;
        },
        get locale(): string {
            return this.parent.locale;
        },
        get isTestEnv() {
            return ['test'].includes(window.application.params.environment);
        },
        get commuId() {
            if (this.isTestEnv) {
                return MapleMCommuId.TEST;
            }

            switch (this.root.regionConfig.code) {
                case '1':
                    return MapleMCommuId.WEST;
                case '3':
                    return MapleMCommuId.TH;
                case '4':
                    return MapleMCommuId.TW;
                case '5':
                    return MapleMCommuId.SEA;
                default:
                    return MapleMCommuId.WEST;
            }
        },
    }))
    .actions((self) => ({
        getInitialCategory(): MapleMCommuCategory {
            if (self.parent.category === MapleMCommuCategory.ALL && self.parent.categories.length === 0) {
                return MapleMCommuCategory.MAIN_NEWS;
            }

            return self.parent.category as MapleMCommuCategory;
        },
        getBoardId(cat: MapleMCommuCategory) {
            if (self.isTestEnv) {
                const TEST_BOARD_ID = {
                    [MapleMCommuCategory.ALL]: 5287,
                    [MapleMCommuCategory.CATEGORY_1]: 2868,
                    [MapleMCommuCategory.CATEGORY_2]: 2869,
                    [MapleMCommuCategory.CATEGORY_3]: 2870,
                };

                return TEST_BOARD_ID[cat as keyof typeof TEST_BOARD_ID] || 5287;
            } else {
                switch (self.root.regionConfig.code) {
                    case '3':
                        const LIVE_TH_BOARD_ID = {
                            [MapleMCommuCategory.ALL]: 2773,
                            [MapleMCommuCategory.ANNOUNCEMENT]: 2776,
                            [MapleMCommuCategory.PATCH_NOTES]: 2777,
                            [MapleMCommuCategory.TOWN_EVENTS]: 2778,
                            [MapleMCommuCategory.TOWN_MARKET]: 2779,
                            [MapleMCommuCategory.TOWN_NEWSLETTER]: 2781,
                        };
                        return LIVE_TH_BOARD_ID[cat as keyof typeof LIVE_TH_BOARD_ID] || 2773;
                    case '4':
                        const LIVE_TW_BOARD_ID = {
                            [MapleMCommuCategory.ALL]: 2723,
                            [MapleMCommuCategory.ANNOUNCEMENT]: 2725,
                            [MapleMCommuCategory.PATCH_NOTES]: 2726,
                            [MapleMCommuCategory.TOWN_EVENTS]: 2727,
                            [MapleMCommuCategory.TOWN_MARKET]: 2728,
                            [MapleMCommuCategory.TOWN_NEWSLETTER]: 2729,
                        };
                        return LIVE_TW_BOARD_ID[cat as keyof typeof LIVE_TW_BOARD_ID] || 2723;
                    case '1':
                    case '5':
                    default:
                        const LIVE_WEST_SEA_BOARD_ID = {
                            [MapleMCommuCategory.ALL]: 2680,
                            [MapleMCommuCategory.ANNOUNCEMENT]: 2691,
                            [MapleMCommuCategory.PATCH_NOTES]: 2690,
                            [MapleMCommuCategory.TOWN_EVENTS]: 2692,
                            [MapleMCommuCategory.TOWN_MARKET]: 2693,
                            [MapleMCommuCategory.TOWN_NEWSLETTER]: 2702,
                        };
                        return LIVE_WEST_SEA_BOARD_ID[cat as keyof typeof LIVE_WEST_SEA_BOARD_ID] || 2680;
                }
            }
        },
        setList: function (list: any[], category: MapleMCommuCategory) {
            const startIdx = mapleMList.findIndex((info) => info.Category === category);
            const endIdx = mapleMList.lastIndexOf((info: any) => info.Category === category);
            if (startIdx !== -1 && endIdx !== -1) {
                const copyList = [...mapleMList];
                copyList.splice(startIdx, endIdx - startIdx + 1, ...list);
                mapleMList = copyList;
            } else {
                mapleMList = [...mapleMList.filter((info) => info.Category !== category), ...list];
            }
            self.listModel = cast([...mapleMList].sort((a, b) => b.CreateDate - a.CreateDate));
        },
    }))
    .actions((self) => ({
        fetchUmbracoList: flow(function* () {
            const res = yield CmsApi.get(`/news/regions/{regionCode}/${self.productId}/${self.locale}/list.json`).catch(() => []);
            self.umbracoList = cast(
                res.slice(0, 10).map((info: any) => {
                    return {
                        ...info,
                        CreateDate: 0,
                        ThreadId: '',
                        BoardTitle: '',
                    };
                })
            );
        }),
        fetchSticky: flow(function* () {
            const res = yield CommunityApi.get(`/api/v1/board/group/${self.getBoardId(MapleMCommuCategory.ALL)}/stickyThreads`, {
                params: {
                    pageSize: 10,
                },
            }).catch(() => []);

            const result = res.data.stickyThreads.map((data: any) => {
                return {
                    Category: MapleMCommuCategory.MAIN_NEWS,
                    Id: data.createDate + Number(data.threadId),
                    ImageThumbnail2: self.parent.isDefaultThumbnail(data.thumbnailImageUrl) ? '' : data.thumbnailImageUrl,
                    Title: data.title,
                    Summary: data.summary,
                    LiveDate: new Date(data.createDate * 1000).toISOString(),
                    CreateDate: data.createDate,
                    ThreadId: data.threadId,
                };
            });
            self.setList(result, MapleMCommuCategory.MAIN_NEWS);
        }),
        fetchBoard: flow(function* (boardId: number, category: MapleMCommuCategory) {
            let url = `/api/v1/board/${boardId}/threadsV2`;
            if (category === MapleMCommuCategory.ALL) {
                url = `/api/v1/board/group/${boardId}/threads`;
            }
            const res = yield CommunityApi.get(url, {
                params: {
                    paginationType: 'MORE',
                    pageSize: 10,
                },
            }).catch(() => []);
            const result = res.data.threads.map((data: any) => {
                return {
                    Category: category,
                    Id: Number(data.threadId),
                    ImageThumbnail2: self.parent.isDefaultThumbnail(data.thumbnailImageUrl) ? '' : data.thumbnailImageUrl,
                    Title: data.title,
                    Summary: data.summary,
                    LiveDate: new Date(data.createDate * 1000).toISOString(),
                    CreateDate: data.createDate,
                    ThreadId: data.threadId,
                    BoardTitle: data.boardTitle || '',
                };
            });
            self.setList(result, category);
        }),
        getCategories() {
            self.parent.setCategory(self.getInitialCategory());
            if (self.isTestEnv) {
                return [
                    MapleMCommuCategory.MAIN_NEWS,
                    MapleMCommuCategory.ALL,
                    MapleMCommuCategory.CATEGORY_1,
                    MapleMCommuCategory.CATEGORY_2,
                    MapleMCommuCategory.CATEGORY_3,
                ];
            } else {
                return [
                    MapleMCommuCategory.MAIN_NEWS,
                    MapleMCommuCategory.ALL,
                    MapleMCommuCategory.ANNOUNCEMENT,
                    MapleMCommuCategory.PATCH_NOTES,
                    MapleMCommuCategory.TOWN_EVENTS,
                    MapleMCommuCategory.TOWN_MARKET,
                    MapleMCommuCategory.TOWN_NEWSLETTER,
                ];
            }
        },
        fetchList() {
            this.fetchUmbracoList();
            this.fetchSticky();
            self.parent.categories
                .filter((cate) => ![MapleMCommuCategory.MAIN_NEWS].includes(cate as MapleMCommuCategory))
                .forEach((cate) => this.fetchBoard(self.getBoardId(cate as MapleMCommuCategory), cate as MapleMCommuCategory));
        },
    }))
    .views((self) => ({
        get filtered() {
            const category: string = self.parent.category;
            if (category === 'All') {
                return self.listModel.filter((x) => x.Category === MapleMCommuCategory.ALL);
            } else if (category === MapleMCommuCategory.MAIN_NEWS) {
                return [...self.umbracoList, ...self.listModel.filter((x) => x.Category === category)];
            }

            return self.listModel.filter((x) => x.Category === category);
        },
        get currentNews(): INewsModel {
            return this.filtered[self.parent.currentIndex!];
        },
        get prev(): INewsModel {
            return this.filtered[self.parent.currentIndex! - 1];
        },
        get next(): INewsModel {
            return this.filtered[self.parent.currentIndex! + 1];
        },
        get list() {
            return this.filtered;
        },
    }))
    .actions((self) => ({
        setCurrentIndex(idx: number | undefined) {
            self.parent.setCurrentIndex(idx);
        },
        setCurrent(newsId: number | undefined) {
            const index = self.filtered.findIndex((x) => x.Id === newsId);
            this.setCurrentIndex(index >= 0 ? index : undefined);
        },
    }));

export default MapleMStore;
